import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Factory, Play } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuth, useApi } from '@/contexts/AuthContext';

const C = { navy: '#0D1B2A', teal: '#00B4D8', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', slate: '#5C6B7A' };

export const GeneratorCard = ({ onDone }) => {
  const api = useApi();
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [count, setCount] = useState(20);
  const [emailPct, setEmailPct] = useState(20);
  const [active, setActive] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/generator/runs?limit=5');
      setRuns(r.data.runs);
      const running = r.data.runs.find(x => x.status === 'running');
      setActive(running || null);
      return running;
    } catch { return null; }
  }, [api]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (active) {
      pollRef.current = setInterval(async () => {
        const still = await load();
        if (!still) { clearInterval(pollRef.current); toast.success('Generator run complete'); onDone && onDone(); }
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [active?.gen_run_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!['platform_owner', 'mga_admin'].includes(user?.role)) return null;

  const start = async () => {
    try {
      const r = await api.post('/generator/run', { count: Number(count), email_ratio: emailPct / 100 });
      toast.success(`Generator run ${r.data.gen_run_id} started (${r.data.requested} submissions, seed ${r.data.seed})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start'); }
  };

  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="generator-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Factory className="w-4 h-4" style={{ color: C.teal }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Submission Generator</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ background: '#EEF1F5', color: C.slate }}>SYNTHETIC · source: generator</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px]" style={{ color: C.slate }}>Count</span>
          <input type="number" min={1} max={100} value={count} onChange={e => setCount(e.target.value)}
            className="w-16 px-2 py-1 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="generator-count-input" />
          <span className="text-[11px]" style={{ color: C.slate }}>Email intake</span>
          <select value={emailPct} onChange={e => setEmailPct(Number(e.target.value))}
            className="px-2 py-1 text-xs rounded border" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="generator-email-ratio-select">
            {[0, 10, 20, 50].map(p => <option key={p} value={p}>{p}%</option>)}
          </select>
          <button onClick={start} disabled={!!active}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-50"
            style={{ background: C.navy }} data-testid="generator-run-btn">
            <Play className="w-3 h-3" /> {active ? `Running ${active.progress || 0}/${active.requested}` : 'Generate volume'}
          </button>
        </div>
      </div>
      {runs.length > 0 && (
        <div className="mt-3 space-y-1">
          {runs.map(r => (
            <div key={r.gen_run_id} className="flex flex-wrap items-center gap-3 text-[11px] px-2 py-1.5 rounded" style={{ background: '#FAFBFC' }} data-testid={`generator-run-row-${r.gen_run_id}`}>
              <span className="font-mono" style={{ color: C.navy }}>{r.gen_run_id}</span>
              <span style={{ color: r.status === 'complete' ? C.green : C.amber }}>{r.status}{r.status === 'running' ? ` · ${r.progress || 0}/${r.requested}` : ''}</span>
              <span style={{ color: C.slate }}>{r.requested} subs · {Math.round(r.email_ratio * 100)}% email · seed {r.seed}</span>
              {r.aggregates && (
                <span className="ml-auto" style={{ color: C.slate }}>
                  STP {(r.aggregates.stp_rate * 100).toFixed(0)}% · escalated {r.aggregates.escalated} · p50 {r.aggregates.p50_ms}ms · p95 {r.aggregates.p95_ms}ms{r.aggregates.errors > 0 ? ` · ${r.aggregates.errors} errors` : ''}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default GeneratorCard;
